import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faExclamationCircle,
  faArrowLeft,
  faFlag,
  faPlus,
  faTrash,
  faStickyNote,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../../contexts/AuthContext';

interface InstructorStudent {
  userId: string;
  studentRecordId: string;
  studentId: string;
  fullName: string;
  email: string;
  learnershipTitle: string;
  overallProgressPercent: number;
  averageGrade: number | null;
  isFlagged: boolean;
}

interface NoteItem {
  noteId: string;
  note: string;
  flaggedForFollowUp: boolean;
  createdAt: string;
}

export default function InstructorStudentDetailPage() {
  const { studentId } = useParams<{ studentId: string }>();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [student, setStudent] = useState<InstructorStudent | null>(null);
  const [notes, setNotes] = useState<NoteItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newNote, setNewNote] = useState('');
  const [flagNote, setFlagNote] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId]);

  const fetchData = async () => {
    if (!studentId) return;
    try {
      setIsLoading(true);
      setError(null);
      const headers = { Authorization: `Bearer ${accessToken}` };
      const [studentsRes, notesRes] = await Promise.all([
        fetch('/api/instructor/students', { headers }),
        fetch(`/api/studentnotes/student/${studentId}`, { headers }),
      ]);

      if (!studentsRes.ok || !notesRes.ok) throw new Error('Failed to load student');

      const allStudents: InstructorStudent[] = await studentsRes.json();
      const match = allStudents.find((s) => s.studentRecordId === studentId);
      setStudent(match ?? null);
      setNotes(await notesRes.json());
    } catch (err) {
      console.error(err);
      setError('Failed to load student details. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !studentId) return;
    try {
      setIsSaving(true);
      const response = await fetch('/api/studentnotes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${accessToken}` },
        body: JSON.stringify({ studentId, note: newNote, flaggedForFollowUp: flagNote }),
      });
      if (!response.ok) throw new Error('Failed to add note');
      setNewNote('');
      setFlagNote(false);
      await fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: string) => {
    try {
      await fetch(`/api/studentnotes/${noteId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      await fetchData();
    } catch (err) {
      console.error(err);
    }
  };

  const formatDate = (dateStr: string) =>
    new Date(dateStr).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' });

  return (
    <>
      <div className="bg-white border-b border-canvas-300 px-8 py-4 sticky top-0 z-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm font-semibold text-brand-700 hover:text-brand-800 mb-2"
        >
          <FontAwesomeIcon icon={faArrowLeft} className="h-3.5 w-3.5" />
          Back
        </button>
        <h1 className="text-2xl font-bold text-brand-950">{student?.fullName || 'Student'}</h1>
      </div>

      <div className="p-8 max-w-4xl">
        {isLoading && (
          <div className="flex items-center justify-center py-24">
            <FontAwesomeIcon icon={faSpinner} className="h-10 w-10 text-brand-600 animate-spin" />
          </div>
        )}

        {!isLoading && error && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
            <FontAwesomeIcon icon={faExclamationCircle} className="h-8 w-8 text-red-600 mb-3" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {!isLoading && !error && student && (
          <>
            <div className="card-surface p-6 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-brand-900/45 text-xs uppercase font-semibold">Student ID</p>
                  <p className="font-semibold text-brand-950 mt-1">{student.studentId}</p>
                </div>
                <div>
                  <p className="text-brand-900/45 text-xs uppercase font-semibold">Email</p>
                  <p className="font-semibold text-brand-950 mt-1 truncate">{student.email}</p>
                </div>
                <div>
                  <p className="text-brand-900/45 text-xs uppercase font-semibold">Course</p>
                  <p className="font-semibold text-brand-950 mt-1 truncate">{student.learnershipTitle}</p>
                </div>
                <div>
                  <p className="text-brand-900/45 text-xs uppercase font-semibold">Average</p>
                  <p className="font-semibold text-brand-950 mt-1">
                    {student.averageGrade != null ? `${student.averageGrade}%` : '—'}
                  </p>
                </div>
              </div>
            </div>

            <div className="card-surface p-6">
              <h2 className="text-lg font-bold text-brand-950 mb-4 flex items-center gap-2">
                <FontAwesomeIcon icon={faStickyNote} className="h-4 w-4 text-brand-600" />
                Private Notes
              </h2>

              <div className="space-y-3 mb-5">
                <textarea
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                  rows={3}
                  placeholder="Add a private note about this student (only visible to trainers)..."
                  className="w-full px-4 py-2 border border-canvas-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500"
                />
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-brand-900/80">
                    <input
                      type="checkbox"
                      checked={flagNote}
                      onChange={(e) => setFlagNote(e.target.checked)}
                      className="rounded border-canvas-300"
                    />
                    <FontAwesomeIcon icon={faFlag} className="h-3.5 w-3.5 text-red-500" />
                    Flag student for follow-up
                  </label>
                  <button
                    onClick={handleAddNote}
                    disabled={isSaving || !newNote.trim()}
                    className="px-4 py-2 bg-brand-gradient text-white font-semibold rounded-lg hover:shadow-lg transition-all disabled:opacity-50 text-sm flex items-center gap-2"
                  >
                    <FontAwesomeIcon icon={isSaving ? faSpinner : faPlus} className={`h-3.5 w-3.5 ${isSaving ? 'animate-spin' : ''}`} />
                    Add Note
                  </button>
                </div>
              </div>

              {notes.length === 0 ? (
                <p className="text-sm text-brand-900/50">No notes yet.</p>
              ) : (
                <div className="space-y-3">
                  {notes.map((n) => (
                    <div key={n.noteId} className="border border-canvas-200 rounded-xl p-4 flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {n.flaggedForFollowUp && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 text-red-600 text-xs font-semibold rounded-full">
                              <FontAwesomeIcon icon={faFlag} className="h-3 w-3" /> Follow-up
                            </span>
                          )}
                          <span className="text-xs text-brand-900/40">{formatDate(n.createdAt)}</span>
                        </div>
                        <p className="text-sm text-brand-900/80 whitespace-pre-wrap">{n.note}</p>
                      </div>
                      <button
                        onClick={() => handleDeleteNote(n.noteId)}
                        className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg flex-shrink-0"
                      >
                        <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  );
}
